// src/portal-app/shell/LiquidGlass.js
//
// Liquid-Glass primitives (F12b Task 3) — the shared visual language every
// Aqana Manager portal surface is built from. Sibling of the staff POS
// "Aurora Glass" primitive (src/pos-app/shell/AuroraBackground.js) but tuned
// independently with the richer Liquid-Glass recipe approved in
// docs/mockups/f12b/manager-liquid-glass.html:
//
//   blur(20px) saturate(1.8) · translucent surface 30% (veil 44%) ·
//   1px light border · radius 24 · depth shadow 0 8px 32px @ 12% ink ·
//   top sheen · subtle press-lift.
//
//   liquidSurface                 — the one place the glass recipe lives.
//   glass(overrides)              — merged style helper.
//   <GlassCard veil lift as>      — frosted card; `veil` raises surface
//                                   opacity to ~44% for dense-text legibility
//                                   (keeps ink text >= 4.5:1 over the aurora).
//   <LiquidGlassBackground intensity="calm|full">
//                                 — living aurora of brand blues behind the
//                                   glass. calm (default) for working screens,
//                                   full for hero / auth moments. Drift is
//                                   PAUSED under prefers-reduced-motion.
//
// Tokens only: every colour reads --c-* theme vars (light + dark both work).
// Deliberate exception pattern: color-mix(in srgb, var(--c-*) N%, transparent)
// for translucency/glows — the tokens stay the single source of hue.
import React from 'react';
import { FONT } from '../../styles/theme';

/* ------------------------------------------------------------------ */
/* Glass surface — the one place the Liquid-Glass recipe lives.        */
/* Recipe values are named constants so the exported `liquidSurface`   */
/* style object and the `.lqg-card` CSS class (below) can never drift. */
/* ------------------------------------------------------------------ */
const SURFACE_BG = 'color-mix(in srgb, var(--c-surface) 30%, transparent)';
const SURFACE_BLUR = 'blur(20px) saturate(1.8)';
const SURFACE_BORDER = '1px solid color-mix(in srgb, var(--c-surface) 45%, transparent)';
const SURFACE_RADIUS = 24;
// Depth shadow stays slate-dark in BOTH themes (a token-derived shadow
// would flip light in dark mode); rgba slate is the approved recipe value.
const SURFACE_SHADOW = [
    '0 8px 32px rgba(15,23,42,.12)',
    'inset 0 1px 0 color-mix(in srgb, var(--c-surface) 55%, transparent)',
].join(', ');
const SURFACE_TRANSITION = 'transform .18s ease, background .18s ease, box-shadow .18s ease';

export const liquidSurface = {
    position: 'relative',
    background: SURFACE_BG,
    WebkitBackdropFilter: SURFACE_BLUR,
    backdropFilter: SURFACE_BLUR,
    border: SURFACE_BORDER,
    borderRadius: SURFACE_RADIUS,
    boxShadow: SURFACE_SHADOW,
    transition: SURFACE_TRANSITION,
};

/* Veil — raised surface opacity for dense-text cards sitting on busy
   aurora zones; keeps body ink comfortably >= 4.5:1. */
const VEIL_BG = 'color-mix(in srgb, var(--c-surface) 44%, transparent)';

export function glass(overrides = {}) {
    return { ...liquidSurface, ...overrides };
}

/* ------------------------------------------------------------------ */
/* Shared stylesheet — injected once into <head>.                      */
/* GlassCard needs pseudo-element sheen + hover/press states, and the  */
/* aurora needs keyframes; neither is expressible as inline style.     */
/* ------------------------------------------------------------------ */
const CSS = `
/* ---- glass card chrome ---- */
/* Base recipe lives HERE (not inline) so the .lqg-lift:hover overrides
   below can win the cascade — inline background/box-shadow would beat
   any class rule. GlassCard's style prop is reserved for caller overrides. */
.lqg-card{position:relative;-webkit-tap-highlight-color:transparent;
  background:${SURFACE_BG};
  -webkit-backdrop-filter:${SURFACE_BLUR};backdrop-filter:${SURFACE_BLUR};
  border:${SURFACE_BORDER};border-radius:${SURFACE_RADIUS}px;
  box-shadow:${SURFACE_SHADOW};
  transition:${SURFACE_TRANSITION};}
.lqg-veil{background:${VEIL_BG};}
.lqg-card::before{content:'';position:absolute;inset:0;border-radius:inherit;pointer-events:none;opacity:.5;
  background:linear-gradient(180deg,color-mix(in srgb,var(--c-surface) 42%,transparent) 0%,color-mix(in srgb,var(--c-surface) 6%,transparent) 42%);}
.lqg-card>*{position:relative;}
.lqg-lift{cursor:pointer;}
.lqg-lift:hover,.lqg-lift:active{transform:scale(1.02);
  background:color-mix(in srgb,var(--c-surface) 40%,transparent);
  box-shadow:0 14px 40px rgba(15,23,42,.16),inset 0 1px 0 color-mix(in srgb,var(--c-surface) 60%,transparent);}
.lqg-lift.lqg-veil:hover,.lqg-lift.lqg-veil:active{background:color-mix(in srgb,var(--c-surface) 52%,transparent);}

/* ---- aurora backdrop ---- */
.lqg-root{position:fixed;inset:0;overflow:hidden;background:var(--c-bg);font-family:${FONT};-webkit-font-smoothing:antialiased;}
.lqg-layer{position:absolute;inset:0;overflow:hidden;z-index:0;pointer-events:none;
  background:linear-gradient(180deg,color-mix(in srgb,var(--c-green) 7%,var(--c-bg)) 0%,var(--c-bg) 45%,color-mix(in srgb,var(--c-green) 5%,var(--c-bg)) 100%);}
.lqg-blob{position:absolute;border-radius:50%;filter:blur(64px);will-change:transform;}
.lqg-b1{width:min(96vw,480px);height:min(96vw,480px);top:-13%;inset-inline-start:-23%;
  background:radial-gradient(circle at 35% 35%,color-mix(in srgb,var(--c-green) 52%,transparent),transparent 68%);}
.lqg-b2{width:min(88vw,440px);height:min(88vw,440px);top:22%;inset-inline-end:-36%;
  background:radial-gradient(circle at 60% 40%,color-mix(in srgb,var(--c-teal) 44%,transparent),transparent 70%);}
.lqg-b3{width:min(104vw,520px);height:min(104vw,520px);bottom:-18%;inset-inline-start:-15%;
  background:radial-gradient(circle at 50% 50%,color-mix(in srgb,var(--c-greenDark) 40%,transparent),transparent 70%);}
.lqg-b4{width:min(62vw,310px);height:min(62vw,310px);bottom:19%;inset-inline-end:-15%;
  background:radial-gradient(circle at 50% 50%,color-mix(in srgb,var(--c-blue) 34%,transparent),transparent 70%);}

/* full — hero / auth / idle moments: mockup-strength living drift */
.lqg-full .lqg-blob{opacity:1;}
:root[data-theme="dark"] .lqg-full .lqg-blob{opacity:.6;}
.lqg-full .lqg-b1{animation:lqgDrift1 26s ease-in-out infinite;}
.lqg-full .lqg-b2{animation:lqgDrift2 31s ease-in-out infinite;}
.lqg-full .lqg-b3{animation:lqgDrift3 24s ease-in-out infinite;}
.lqg-full .lqg-b4{animation:lqgDrift1 36s ease-in-out infinite reverse;}

/* calm — working screens stared at all day: dimmer, glacial ambience */
.lqg-calm .lqg-blob{opacity:.55;}
:root[data-theme="dark"] .lqg-calm .lqg-blob{opacity:.32;}
.lqg-calm .lqg-b1{animation:lqgCalm1 110s ease-in-out infinite alternate;}
.lqg-calm .lqg-b2{animation:lqgCalm2 130s ease-in-out infinite alternate;}
.lqg-calm .lqg-b3{animation:lqgCalm3 120s ease-in-out infinite alternate;}
.lqg-calm .lqg-b4{animation:lqgCalm1 145s ease-in-out infinite alternate-reverse;}

@keyframes lqgDrift1{0%,100%{transform:translate(0,0) scale(1);}50%{transform:translate(12vw,4vh) scale(1.12);}75%{transform:translate(-6vw,7vh) scale(.96);}}
@keyframes lqgDrift2{0%,100%{transform:translate(0,0) scale(1);}50%{transform:translate(-13vw,3vh) scale(1.08);}75%{transform:translate(-5vw,-5vh) scale(1.15);}}
@keyframes lqgDrift3{0%,100%{transform:translate(0,0) scale(1);}50%{transform:translate(10vw,-5vh) scale(1.1);}75%{transform:translate(-8vw,-2vh) scale(1.02);}}
@keyframes lqgCalm1{from{transform:translate(0,0) scale(1);}to{transform:translate(2vw,1vh) scale(1.04);}}
@keyframes lqgCalm2{from{transform:translate(0,0) scale(1.02);}to{transform:translate(-1.6vw,1.4vh) scale(.99);}}
@keyframes lqgCalm3{from{transform:translate(0,0) scale(1);}to{transform:translate(-1.2vw,-1.5vh) scale(1.03);}}

@media (prefers-reduced-motion: reduce){
  .lqg-blob{animation-play-state:paused !important;}
  .lqg-card,.lqg-lift:hover,.lqg-lift:active{transition:none;transform:none;}
}

.lqg-content{position:relative;z-index:1;height:100%;display:flex;flex-direction:column;box-sizing:border-box;}
`;

let cssInjected = false;
function ensureCss() {
    if (cssInjected || typeof document === 'undefined') return;
    const tag = document.createElement('style');
    tag.id = 'lqg-css';
    tag.textContent = CSS;
    document.head.appendChild(tag);
    cssInjected = true;
}

/* ------------------------------------------------------------------ */
/* GlassCard — a frosted surface.                                      */
/*   veil  — raise opacity to ~44% for dense-text legibility.          */
/*   lift  — hover/press scale + brighten (interactive cards).         */
/*   as    — element/component to render (default 'div').              */
/* ------------------------------------------------------------------ */
export function GlassCard({
    as: Tag = 'div',
    veil = false,
    lift = false,
    className = '',
    style,
    children,
    ...rest
}) {
    ensureCss();
    const cls = [
        'lqg-card',
        veil && 'lqg-veil',
        lift && 'lqg-lift',
        className,
    ].filter(Boolean).join(' ');
    // Base recipe + veil come from the .lqg-card / .lqg-veil classes so the
    // .lqg-lift hover rules can override them; inline style is caller-only.
    return (
        <Tag className={cls} style={style} {...rest}>
            {children}
        </Tag>
    );
}

/* ------------------------------------------------------------------ */
/* LiquidGlassBackground — living aurora of Aqana blues behind glass.  */
/* ------------------------------------------------------------------ */
export function LiquidGlassBackground({ intensity = 'calm', children }) {
    ensureCss();
    const mode = intensity === 'full' ? 'lqg-full' : 'lqg-calm';

    return (
        <div className={`lqg-root ${mode}`}>
            <div className="lqg-layer" aria-hidden="true">
                <div className="lqg-blob lqg-b1" />
                <div className="lqg-blob lqg-b2" />
                <div className="lqg-blob lqg-b3" />
                <div className="lqg-blob lqg-b4" />
            </div>
            <div className="lqg-content">{children}</div>
        </div>
    );
}

export default LiquidGlassBackground;
